import React, { useEffect, useMemo, useState } from "react";

import { listarModulos, obterModulosLicenca, obterPrecosModulosRevenda } from "../../services/api";

import {

  CODIGOS_PADRAO,

  catalogoCompletoPorLicenca,

  filtrarModulosPorLicenca,

  formatBRL,

  idsFromCodigos,

  moduloApenasContador,

  moduloOcultoUI,

  moduloUsaQuantidade,

  nomeModuloUI,

  quantidadesFromItens,

  selectedIdsFromItens,

  UNIDADE_LABEL,

} from "../../utils/modulosLicenca";



/**

 * Seleção de módulos da licença.

 * onChange recebe { selectedIds, quantidades, catalogo, licencaTipo }.

 */

const SeletorModulosLicenca = ({

  token,

  licencaTipo,

  licencaId = null,

  selectedIds,

  quantidades = {},

  onChange,

  disabled = false,

}) => {

  const [catalogoVisivel, setCatalogoVisivel] = useState([]);
  const [catalogoCompleto, setCatalogoCompleto] = useState([]);

  const [precos, setPrecos] = useState({});

  const [exibirPrecos, setExibirPrecos] = useState(false);

  const [loading, setLoading] = useState(true);

  const [erro, setErro] = useState("");



  const emitir = (ids, qtds, cat, tipo = licencaTipo) => {
    const catalogo = cat ?? catalogoCompleto;
    if (cat?.length) setCatalogoCompleto(cat);
    onChange({
      selectedIds: ids,
      quantidades: qtds,
      catalogo,
      licencaTipo: tipo,
    });
  };



  useEffect(() => {

    let cancelado = false;



    const carregar = async () => {

      setLoading(true);

      setErro("");

      try {

        const resp = await listarModulos(token, { ativo: true });

        const todos = Array.isArray(resp?.data) ? resp.data : [];

        const completo = catalogoCompletoPorLicenca(todos, licencaTipo);

        const visiveis = filtrarModulosPorLicenca(todos, licencaTipo);

        if (cancelado) return;

        setCatalogoVisivel(visiveis);



        try {

          const precosResp = await obterPrecosModulosRevenda(token);

          const listaPrecos = Array.isArray(precosResp?.data) ? precosResp.data : [];

          if (listaPrecos.length > 0) {

            const map = {};

            listaPrecos.forEach((p) => {

              map[p.moduloId] = p;

            });

            setPrecos(map);

            setExibirPrecos(true);

          }

        } catch {

          setPrecos({});

          setExibirPrecos(false);

        }



        if (licencaId) {

          try {

            const licResp = await obterModulosLicenca(licencaTipo, licencaId, token);

            const itens = Array.isArray(licResp?.data) ? licResp.data : [];

            const ids = selectedIdsFromItens(itens, completo, licencaTipo);

            const qtds = quantidadesFromItens(itens, licencaTipo);

            if (itens.length > 0) {

              emitir(ids, qtds, completo, licencaTipo);

              return;

            }

          } catch {

            // licença sem módulos ainda — usa padrão

          }

        }



        const padraoIds = idsFromCodigos(completo, CODIGOS_PADRAO[licencaTipo] || []).filter(

          (modId) => {

            const mod = completo.find((m) => m.id === modId);

            return mod && !moduloApenasContador(mod.codigo, licencaTipo);

          }

        );

        emitir(padraoIds, {}, completo, licencaTipo);

      } catch (e) {

        if (!cancelado) {

          setErro("Não foi possível carregar os módulos.");

          console.error(e);

        }

      } finally {

        if (!cancelado) setLoading(false);

      }

    };



    carregar();

    return () => { cancelado = true; };

    // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [token, licencaTipo, licencaId]);



  const grupos = useMemo(() => {

    const base = catalogoVisivel.filter((m) => m.tipo === "base");

    const adicional = catalogoVisivel.filter((m) => m.tipo === "adicional");

    return { base, adicional };

  }, [catalogoVisivel]);



  const toggle = (modulo) => {

    if (disabled) return;

    const set = new Set(selectedIds || []);

    const qtds = { ...quantidades };

    if (set.has(modulo.id)) {

      set.delete(modulo.id);

    } else {

      set.add(modulo.id);

      if (moduloUsaQuantidade(modulo.codigo, licencaTipo) && !qtds[modulo.id]) {

        qtds[modulo.id] = 1;

      }

    }

    emitir([...set], qtds);

  };



  const alterarQtd = (modulo, delta, apenasContador = false) => {

    if (disabled) return;

    const min = apenasContador ? 0 : 1;

    const atual = Math.max(min, Number(quantidades[modulo.id]) || (apenasContador ? 0 : 1));

    const proxima = Math.min(99, Math.max(min, atual + delta));

    emitir(selectedIds || [], { ...quantidades, [modulo.id]: proxima });

  };



  const precoLabel = (moduloId) => {

    const preco = precos[moduloId];

    if (!exibirPrecos || !preco) return null;

    return `${formatBRL(preco.valorUnitario)}${UNIDADE_LABEL[preco.unidadeCobranca] || ""}`;

  };

  const totalEstimado = useMemo(() => {
    if (!exibirPrecos) return null;

    let total = 0;

    // Módulos always-on (ocultos na UI) com preço
    (catalogoCompleto || []).forEach((m) => {
      if (!moduloOcultoUI(m.codigo, licencaTipo)) return;
      const preco = precos[m.id];
      if (!preco) return;
      total += Number(preco.valorUnitario) || 0;
    });

    // Visíveis selecionados / contadores
    (catalogoVisivel || []).forEach((m) => {
      const preco = precos[m.id];
      if (!preco) return;
      const unit = Number(preco.valorUnitario) || 0;

      if (moduloApenasContador(m.codigo, licencaTipo)) {
        const qtd = Math.max(0, Number(quantidades[m.id]) || 0);
        total += unit * qtd;
        return;
      }

      if (!(selectedIds || []).includes(m.id)) return;
      const qtd = moduloUsaQuantidade(m.codigo, licencaTipo)
        ? Math.max(1, Number(quantidades[m.id]) || 1)
        : 1;
      total += unit * qtd;
    });

    return total;
  }, [
    exibirPrecos,
    precos,
    catalogoCompleto,
    catalogoVisivel,
    selectedIds,
    quantidades,
    licencaTipo,
  ]);



  const renderModuloContador = (m) => {

    const qtd = Math.max(0, Number(quantidades[m.id]) || 0);

    const ativo = qtd > 0;

    const preco = precoLabel(m.id);

    return (

      <div

        key={m.id}

        className={`

          flex items-center justify-between gap-3 p-3 rounded-xl border transition

          ${ativo

            ? "border-blue-500/60 bg-blue-50/80 dark:bg-blue-500/10"

            : "border-gray-200 dark:border-white/10"}

          ${disabled ? "opacity-60" : ""}

        `}

      >

        <span className="min-w-0">

          <span className="block text-sm font-medium text-gray-900 dark:text-white">

            {nomeModuloUI(m, licencaTipo)}

            {preco && (

              <span className="ml-2 text-xs font-semibold text-emerald-700 dark:text-emerald-300">

                {preco}

              </span>

            )}

          </span>

          {m.descricao ? (

            <span className="block text-xs text-gray-500 dark:text-slate-400 mt-0.5">{m.descricao}</span>

          ) : null}

        </span>

        <div className="flex items-center gap-1 shrink-0">

          <button

            type="button"

            className="h-7 w-7 rounded-lg border border-gray-300 dark:border-white/20 text-sm font-semibold"

            disabled={disabled || qtd <= 0}

            onClick={() => alterarQtd(m, -1, true)}

            aria-label={`Diminuir ${nomeModuloUI(m, licencaTipo)}`}

          >

            −

          </button>

          <span className="w-7 text-center text-sm font-semibold tabular-nums">{qtd}</span>

          <button

            type="button"

            className="h-7 w-7 rounded-lg border border-gray-300 dark:border-white/20 text-sm font-semibold"

            disabled={disabled || qtd >= 99}

            onClick={() => alterarQtd(m, 1, true)}

            aria-label={`Aumentar ${nomeModuloUI(m, licencaTipo)}`}

          >

            +

          </button>

        </div>

      </div>

    );

  };



  const renderModuloCheckbox = (m) => {

    const checked = (selectedIds || []).includes(m.id);

    const usaQtd = moduloUsaQuantidade(m.codigo, licencaTipo) && !moduloApenasContador(m.codigo, licencaTipo);

    const qtd = Math.max(1, Number(quantidades[m.id]) || 1);

    const preco = precoLabel(m.id);

    return (

      <div

        key={m.id}

        className={`

          flex items-start gap-3 p-3 rounded-xl border transition

          ${checked

            ? "border-blue-500/60 bg-blue-50/80 dark:bg-blue-500/10"

            : "border-gray-200 dark:border-white/10 hover:border-gray-300 dark:hover:border-white/20"}

          ${disabled ? "opacity-60" : ""}

        `}

      >

        <label className={`flex items-start gap-3 flex-1 min-w-0 ${disabled ? "cursor-not-allowed" : "cursor-pointer"}`}>

          <input

            type="checkbox"

            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"

            checked={checked}

            disabled={disabled || loading}

            onChange={() => toggle(m)}

          />

          <span>

            <span className="block text-sm font-medium text-gray-900 dark:text-white">

              {nomeModuloUI(m, licencaTipo)}

              {preco && (

                <span className="ml-2 text-xs font-semibold text-emerald-700 dark:text-emerald-300">

                  {preco}

                </span>

              )}

            </span>

            {m.descricao ? (

              <span className="block text-xs text-gray-500 dark:text-slate-400 mt-0.5">{m.descricao}</span>

            ) : null}

          </span>

        </label>

        {usaQtd && checked && (

          <div className="flex items-center gap-1 shrink-0">

            <button

              type="button"

              className="h-7 w-7 rounded-lg border border-gray-300 dark:border-white/20 text-sm font-semibold"

              disabled={disabled || qtd <= 1}

              onClick={() => alterarQtd(m, -1)}

              aria-label={`Diminuir ${nomeModuloUI(m, licencaTipo)}`}

            >

              −

            </button>

            <span className="w-7 text-center text-sm font-semibold tabular-nums">{qtd}</span>

            <button

              type="button"

              className="h-7 w-7 rounded-lg border border-gray-300 dark:border-white/20 text-sm font-semibold"

              disabled={disabled || qtd >= 99}

              onClick={() => alterarQtd(m, 1)}

              aria-label={`Aumentar ${nomeModuloUI(m, licencaTipo)}`}

            >

              +

            </button>

          </div>

        )}

      </div>

    );

  };



  const renderGrupo = (titulo, itens) => {

    if (!itens.length) return null;

    return (

      <div className="mb-4">

        <h4 className="text-sm font-semibold text-gray-800 dark:text-slate-100 mb-2 uppercase tracking-wide">

          {titulo}

        </h4>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">

          {itens.map((m) =>

            moduloApenasContador(m.codigo, licencaTipo)

              ? renderModuloContador(m)

              : renderModuloCheckbox(m)

          )}

        </div>

      </div>

    );

  };



  return (

    <div className="zen-card rounded-2xl p-4 dark:bg-white/5 dark:border-white/10">

      <div className="mb-3">

        <h3 className="text-base font-semibold text-gray-900 dark:text-white">Módulos da licença</h3>

        <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">

          {licencaTipo === "ZenGestao" ? (

            <>

              Inclui 1 PDV, gestão, cadastros, estoque, fornecedores e NF-e.

              Use os contadores para auto atendimento, PDVs adicionais, empresas, WhatsApp, inventário, pré-venda, ZenPay e multi estoque.

            </>

          ) : licencaTipo === "ZenPDV" ? (

            <>

              Inclui 1 terminal ZenPDV Desktop (Windows). Use o contador para PDVs adicionais além do primeiro.

            </>

          ) : (

            <>

              Marque os módulos habilitados nesta licença.

            </>

          )}

          {exibirPrecos ? " Os valores exibidos são da tabela vinculada à sua revenda." : ""}

        </p>

      </div>



      {loading && (

        <p className="text-sm text-gray-500 dark:text-slate-400">Carregando módulos...</p>

      )}

      {erro && <p className="text-sm text-red-500">{erro}</p>}



      {!loading && !erro && (

        <>

          {renderGrupo("Adicionais", grupos.adicional)}

          {renderGrupo("Opcionais", grupos.base)}

          {exibirPrecos && totalEstimado != null ? (
            <div className="mt-4 pt-3 border-t border-gray-200 dark:border-white/10 flex items-center justify-between gap-3">
              <span className="text-sm font-medium text-gray-700 dark:text-slate-200">
                Total estimado da licença
              </span>
              <span className="text-base font-bold text-emerald-700 dark:text-emerald-300 tabular-nums">
                {formatBRL(totalEstimado)}
                <span className="text-xs font-semibold text-gray-500 dark:text-slate-400 ml-1">/mês</span>
              </span>
            </div>
          ) : null}

        </>

      )}

    </div>

  );

};



export default SeletorModulosLicenca;


